import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { lightTheme, darkTheme } from "../styles/theme";
import { CompanyIcon as CompanyIconSVG } from "./Experience/CompanyIcons";
import { RandomFloatingIcons, DEVOPS_ICONS } from "./TechStackIcons";

const ExperienceSection = styled.section<{ theme: "light" | "dark" }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 60px 4% 30px;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) =>
    theme === "light" ? "#ffffff" : darkTheme.background};

  /* Neo-brutalism grid pattern */
  background-image: ${({ theme }) =>
    theme === "light"
      ? `
          linear-gradient(rgba(77, 159, 255, 0.1) 2px, transparent 2px),
          linear-gradient(90deg, rgba(77, 159, 255, 0.1) 2px, transparent 2px),
          linear-gradient(rgba(77, 159, 255, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(77, 159, 255, 0.05) 1px, transparent 1px)
        `
      : `
          linear-gradient(rgba(107, 159, 255, 0.08) 2px, transparent 2px),
          linear-gradient(90deg, rgba(107, 159, 255, 0.08) 2px, transparent 2px),
          linear-gradient(rgba(107, 159, 255, 0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(107, 159, 255, 0.04) 1px, transparent 1px)
        `};
  background-size: ${({ theme }) =>
    theme === "light"
      ? `100px 100px, 100px 100px, 20px 20px, 20px 20px`
      : `100px 100px, 100px 100px, 20px 20px, 20px 20px`};
  background-position:
    -2px -2px,
    -2px -2px,
    -1px -1px,
    -1px -1px;

  /* Corner accent blocks */
  &::before {
    content: "";
    position: absolute;
    top: 10%;
    right: 5%;
    width: 120px;
    height: 120px;
    background: ${({ theme }) =>
      theme === "light"
        ? `rgba(77, 159, 255, 0.08)`
        : `rgba(107, 159, 255, 0.05)`};
    border: 3px solid
      ${({ theme }) =>
        theme === "light"
          ? `rgba(77, 159, 255, 0.2)`
          : `rgba(107, 159, 255, 0.15)`};
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 15%;
    left: 8%;
    width: 80px;
    height: 80px;
    background: ${({ theme }) =>
      theme === "light"
        ? `rgba(77, 159, 255, 0.06)`
        : `rgba(107, 159, 255, 0.04)`};
    border: 3px solid
      ${({ theme }) =>
        theme === "light"
          ? `rgba(77, 159, 255, 0.18)`
          : `rgba(107, 159, 255, 0.12)`};
    transform: rotate(15deg);
    pointer-events: none;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 70px 10% 40px;
  }
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)<{ theme: "light" | "dark" }>`
  display: inline-block;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  padding: 6px 12px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};

  @media (min-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 0.8rem;
  overflow-x: hidden;
  max-width: 100vw;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 30px;
    margin-top: 1.2rem;
    overflow-x: visible;
    max-width: none;
  }

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 200px 1fr 300px;
    gap: 30px;
    align-items: start;
  }
`;

const CompanyImageWrapper = styled(motion.div)<{ theme: "light" | "dark" }>`
  display: none;

  @media (min-width: 1024px) {
    display: block;
    position: relative;
    width: 100%;
    height: 300px;
    border: 4px solid
      ${({ theme }) =>
        theme === "light" ? lightTheme.border : darkTheme.border};
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
    background: ${({ theme }) =>
      theme === "light" ? lightTheme.background : darkTheme.surface};
    overflow: hidden;
    transform: rotate(-2deg);
    transition: transform 0.3s ease;

    &:hover {
      transform: rotate(0deg) scale(1.02);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      padding: 20px;
    }
  }
`;

const CompanyPlaceholder = styled.div<{ theme: "light" | "dark" }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme === "light"
      ? "linear-gradient(135deg, #f0f4ff 0%, #e6eeff 100%)"
      : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"};
  padding: 20px;
  text-align: center;
`;


const TabList = styled.div<{ theme: "light" | "dark" }>`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 100%;
  border-bottom: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  padding-bottom: 4px;
  margin-bottom: 0.8rem;
  max-width: 100vw;
  overscroll-behavior-x: contain;
  gap: 6px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    background: transparent;
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (min-width: 768px) {
    flex-direction: column;
    min-width: 200px;
    border-bottom: none;
    border-left: ${({ theme }) =>
      theme === "light" ? lightTheme.border : darkTheme.border};
    margin-bottom: 0;
    padding-bottom: 0;
    max-width: none;
    overscroll-behavior-x: auto;
    gap: 0;
  }
`;

const TabButton = styled.button<{ isActive: boolean; theme: "light" | "dark" }>`
  padding: 8px 12px;
  text-align: center;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
  color: ${(props) =>
    props.isActive
      ? props.theme === "light"
        ? lightTheme.background
        : darkTheme.background
      : props.theme === "light"
        ? lightTheme.text
        : darkTheme.text};
  background-color: ${(props) =>
    props.isActive
      ? props.theme === "light"
        ? lightTheme.accent
        : darkTheme.accent
      : props.theme === "light"
        ? lightTheme.surface
        : darkTheme.surface};
  border: 3px solid
    ${(props) =>
      props.theme === "light" ? lightTheme.border : darkTheme.border};
  box-shadow: ${(props) =>
    props.theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  transition: transform 0.15s ease;

  &:hover {
    transform: translate(-2px, -2px);
  }

  @media (min-width: 768px) {
    margin-left: 0;
  }
`;

const TabContentWrapper = styled.div`
  flex: 1;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 400px;
  overflow: hidden;
  isolation: isolate;

  @media (min-width: 768px) {
    width: auto;
    min-height: 100px;
  }
`;

const TabContent = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 0 4px;
  will-change: opacity;

  @media (min-width: 768px) {
    position: relative;
    padding: 0;
  }
`;

const JobTitle = styled.h3<{ theme: "light" | "dark" }>`
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 1.3rem;
  }

  span {
    color: ${({ theme }) =>
      theme === "light" ? lightTheme.accent : darkTheme.accent};
  }

  a {
    color: ${({ theme }) =>
      theme === "light" ? lightTheme.accent : darkTheme.accent};
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const JobDuration = styled.p<{ theme: "light" | "dark" }>`
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  font-size: 0.85rem;
  margin-bottom: 0.8rem;

  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const JobDescription = styled.ul<{ theme: "light" | "dark" }>`
  list-style-type: none;
  padding-left: 0;
  margin-top: 0;

  li {
    position: relative;
    padding-left: 16px;
    margin-bottom: 0.6rem;
    color: ${({ theme }) =>
      theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
    font-size: 0.9rem;
    line-height: 1.6;

    &:before {
      content: "▹";
      position: absolute;
      left: 0;
      color: ${({ theme }) =>
        theme === "light" ? lightTheme.accent : darkTheme.accent};
    }

    @media (min-width: 768px) {
      font-size: 1rem;
      margin-bottom: 0.75rem;
    }
  }
`;

interface Job {
  company: string;
  companyUrl?: string;
  title: string;
  duration: string;
  description: string[];
  image?: string;
  icon?: string;
  scale?: number;
}

const Experience: React.FC = () => {
  const { theme } = useTheme();
  const jobs: Job[] = [
    {
      company: "Arent",
      companyUrl: "https://arent.co.jp/en/",
      title: "Software Engineer",
      duration: "December 2025 - Present",
      description: [
        "Contributed to digital-transformation projects in the construction domain, developing desktop and web applications for material management (procurement, inventory, supplier portals).",
        "Built desktop tools for AutoCAD-related configuration, analyzing customer documents and business workflows to propose and implement technical solutions.",
        "Maintained documentation, supported testing and deployments, and worked with stakeholders to prioritize deliverables.",
      ],
      image: "/icons/3d.webp",
      scale: 1.2,
    },
    {
      company: "MindX Technology School",
      companyUrl: "http://mindx.edu.vn/",
      title: "Engineering Team Lead",
      duration: "September 2023 - November 2025",
      description: [
        "Led a 9–12 engineer team building internal ERP systems (CRM, HRM, Finance, Legal).",
        "Migrated CRM to microservices and introduced AI automations to streamline operations.",
        "Led technical hiring, onboarding, and mentoring.",
      ],
      image: "/icons/scholarship.webp",
      scale: 1.35,
    },
    {
      company: "SotaNext JSC",
      companyUrl: "https://sotanext.com/",
      title: "Software Engineer",
      duration: "April 2021 - September 2023",
      description: [
        "Developed Monsterra NFT Game ecosystem serving 300,000+ players with 25,000+ daily active users",
        "Integrated multi-chain infrastructure including BNB, OKX, TERRA, AVAX, and AURA",
        "Led development of NFT market system and Bridge system",
        "Designed and implemented medical chain system for hospital data sharing",
        "Architected and developed Autharium NFT trading platform",
      ],
      image: "/icons/token.webp",
      scale: 1.4,
    },
    {
      company: "Phong Linh Tech",
      title: "Software Engineer",
      duration: "February 2021 - April 2021",
      description: [
        "Led frontend development for AnyGoNow home care services system",
        "Managed a team of 10 part-time members using Scrum/Agile methods",
        "Designed and implemented responsive user interfaces using React and Flutter",
      ],
      image: "/icons/question.webp",
      scale: 1.3,
    },
    {
      company: "Revotech",
      title: "Software Engineer Intern",
      duration: "September 2020 - February 2021",
      description: [
        "Migrated frontend projects from AngularJS to VueJS",
        "Developed new desktop GUI for Online Petrophysical Software",
        "Implemented real-time well data management features",
      ],
      image: "/icons/gear.webp",
      scale: 1,
    },
  ];

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      tabRefs.current[activeTabIndex] &&
      tabListRef.current &&
      window.innerWidth < 768
    ) {
      const button = tabRefs.current[activeTabIndex];
      const container = tabListRef.current;

      if (button && container) {
        requestAnimationFrame(() => {
          const buttonRect = button.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const scrollLeft = container.scrollLeft;

          const targetScroll =
            scrollLeft +
            buttonRect.left -
            containerRect.left -
            containerRect.width / 2 +
            buttonRect.width / 2;

          container.scrollTo({
            left: targetScroll,
            behavior: "smooth",
          });
        });
      }
    }
  }, [activeTabIndex, jobs.length]);

  return (
    <ExperienceSection id="experience" theme={theme}>
      {/* Floating Tech Stack Icons */}
      <RandomFloatingIcons theme={theme} icons={DEVOPS_ICONS} count={3} />

      <ContentWrapper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SectionTitle theme={theme}>Where I've Worked</SectionTitle>
        <TabContainer>
          <TabList theme={theme} ref={tabListRef}>
            {jobs.map((job, index) => (
              <TabButton
                key={job.company}
                isActive={activeTabIndex === index}
                theme={theme}
                onClick={() => setActiveTabIndex(index)}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
              >
                {job.company}
              </TabButton>
            ))}
          </TabList>
          <TabContentWrapper>
            <AnimatePresence initial={false} mode="wait">
              <TabContent
                key={activeTabIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <JobTitle theme={theme}>
                  {jobs[activeTabIndex].title}{" "}
                  <span>
                    @{" "}
                    <a
                      href={jobs[activeTabIndex].companyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {jobs[activeTabIndex].company}
                    </a>
                  </span>
                </JobTitle>
                <JobDuration theme={theme}>
                  {jobs[activeTabIndex].duration}
                </JobDuration>
                <JobDescription theme={theme}>
                  {jobs[activeTabIndex].description.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </JobDescription>
              </TabContent>
            </AnimatePresence>
          </TabContentWrapper>

          <AnimatePresence initial={false} mode="wait">
            <CompanyImageWrapper
              theme={theme}
              key={`company-${activeTabIndex}`}
              initial={
                activeTabIndex === 0
                  ? { opacity: 0, y: -30, rotate: -5 }
                  : activeTabIndex === 1
                    ? { opacity: 0, x: 50, rotate: 5 }
                    : activeTabIndex === 2
                      ? { opacity: 0, scale: 0.8, rotate: -10 }
                      : { opacity: 0, y: 30, rotate: 3 }
              }
              animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: -2 }}
              exit={
                activeTabIndex === 0
                  ? { opacity: 0, y: 30, rotate: 0 }
                  : activeTabIndex === 1
                    ? { opacity: 0, x: -50, rotate: -5 }
                    : activeTabIndex === 2
                      ? { opacity: 0, scale: 1.1, rotate: 5 }
                      : { opacity: 0, y: -30, rotate: -3 }
              }
              transition={{
                duration: 0.4,
                ease:
                  activeTabIndex === 1
                    ? "backOut"
                    : activeTabIndex === 2
                      ? "anticipate"
                      : "easeInOut",
              }}
            >
              {jobs[activeTabIndex].image ? (
                <img
                  src={jobs[activeTabIndex].image}
                  alt={jobs[activeTabIndex].company}
                  style={{
                    transform: `scale(${jobs[activeTabIndex].scale})`,
                  }}
                />
              ) : (
                <CompanyPlaceholder theme={theme}>
                  <CompanyIconSVG
                    company={jobs[activeTabIndex].company}
                    theme={theme}
                  />
                </CompanyPlaceholder>
              )}
            </CompanyImageWrapper>
          </AnimatePresence>
        </TabContainer>
      </ContentWrapper>
    </ExperienceSection>
  );
};

export default Experience;
